import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NgControl } from '@angular/forms';

export interface HpSelectOption {
  label: string;
  value: any;
  disabled?: boolean;
}

/**
 * HpSelectComponent — HP Design System custom select / dropdown.
 *
 * Drop-in replacement for native <select> that integrates with Angular
 * reactive forms via ControlValueAccessor. Uses fixed-position dropdown
 * to escape overflow-hidden and overflow-y-auto ancestors.
 *
 * Usage:
 *   <app-hp-select
 *     formControlName="myField"
 *     [options]="options"
 *     placeholder="Choose..."
 *     [disabled]="isLoading"
 *     (selectionChange)="onSelect($event)"
 *   />
 *
 * Options shape: { label: string; value: any; disabled?: boolean }[]
 * The `value` is what gets written to the form control — can be any type
 * including full objects (equivalent to [ngValue] on native <option>).
 */
@Component({
  selector: 'app-hp-select',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative w-full">
      <!-- Trigger -->
      <button
        type="button"
        class="w-full h-8 bg-hp-chip rounded px-2.5 flex items-center justify-between gap-1.5 font-hp-sans text-[11px] outline-none transition-colors border cursor-pointer hover:bg-black/[0.02] disabled:opacity-50 disabled:cursor-not-allowed"
        [ngClass]="{
          'border-hp-crit': isInvalid && !isOpen,
          'border-hp-accent': isOpen,
          'border-black/10': !isInvalid && !isOpen
        }"
        [disabled]="isDisabled"
        (click)="toggle()"
        #triggerEl
      >
        <span class="truncate leading-none"
              [class.text-hp-muted2]="!selectedLabel"
              [class.text-hp-ink]="!!selectedLabel">
          {{ selectedLabel || placeholder }}
        </span>
        <svg
          viewBox="0 0 24 24" width="9" height="9" fill="none"
          stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round"
          class="shrink-0 text-hp-muted"
          style="transition: transform 120ms ease; flex-shrink:0"
          [style.transform]="isOpen ? 'rotate(180deg)' : 'rotate(0deg)'"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <!-- Dropdown — fixed position to escape overflow containers -->
      @if (isOpen) {
        <div
          class="fixed z-[500] rounded-[8px] bg-hp-panel border border-black/[0.12] shadow-[0_8px_28px_rgba(20,18,14,0.10),0_2px_8px_rgba(20,18,14,0.06)] overflow-hidden"
          [style.top.px]="dropdownTop"
          [style.left.px]="dropdownLeft"
          [style.width.px]="dropdownWidth"
        >
          <div class="flex flex-col overflow-y-auto" style="max-height: 180px">
            @for (opt of options; track opt.label) {
              <button
                type="button"
                class="w-full flex items-center justify-between px-2.5 py-[7px] text-left font-hp-sans text-[11px] transition-colors hover:bg-hp-panel-deep"
                [ngClass]="{
                  'opacity-40 pointer-events-none text-hp-ink': opt.disabled,
                  'text-hp-accent': !opt.disabled && isSelected(opt),
                  'text-hp-ink cursor-pointer': !opt.disabled && !isSelected(opt),
                  'cursor-pointer': !opt.disabled && isSelected(opt)
                }"
                (click)="select(opt)"
              >
                <span>{{ opt.label }}</span>
                @if (isSelected(opt)) {
                  <svg
                    viewBox="0 0 24 24" width="10" height="10" fill="none"
                    stroke="currentColor" stroke-width="2.5"
                    stroke-linecap="round" stroke-linejoin="round"
                    class="shrink-0"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                }
              </button>
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class HpSelectComponent implements ControlValueAccessor, OnInit {
  @Input() options: HpSelectOption[] = [];
  @Input() placeholder = 'Select...';
  @Input() disabled = false;
  @Output() selectionChange = new EventEmitter<any>();

  isOpen = false;
  selectedValue: any = null;
  dropdownTop = 0;
  dropdownLeft = 0;
  dropdownWidth = 0;

  private _ctrlDisabled = false;
  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};
  private cdr = inject(ChangeDetectorRef);
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl) ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.ngControl?.control?.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  get isDisabled(): boolean {
    return this.disabled || this._ctrlDisabled;
  }

  get selectedLabel(): string {
    if (this.selectedValue == null || this.selectedValue === '') return '';
    const found = this.options.find(o => this.valuesEqual(o.value, this.selectedValue));
    return found?.label ?? '';
  }

  get isInvalid(): boolean {
    const ctrl = this.ngControl?.control;
    return !!(ctrl?.invalid && (ctrl?.dirty || ctrl?.touched));
  }

  isSelected(opt: HpSelectOption): boolean {
    return this.valuesEqual(opt.value, this.selectedValue);
  }

  private valuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a === 'object' && typeof b === 'object') {
      if ('value' in a && 'value' in b) return a.value === b.value;
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return String(a) === String(b);
  }

  toggle(): void {
    if (this.isDisabled) return;
    if (!this.isOpen) {
      const btn = (this.el.nativeElement as HTMLElement).querySelector('button') as HTMLElement;
      const rect = btn.getBoundingClientRect();
      this.dropdownTop = rect.bottom + 4;
      this.dropdownLeft = rect.left;
      this.dropdownWidth = rect.width;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) this.onTouched();
    this.cdr.markForCheck();
  }

  select(opt: HpSelectOption): void {
    if (opt.disabled) return;
    this.selectedValue = opt.value;
    this.isOpen = false;
    this.onChange(opt.value);
    this.selectionChange.emit(opt.value);
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (this.isOpen && !(this.el.nativeElement as HTMLElement).contains(e.target as Node)) {
      this.isOpen = false;
      this.cdr.markForCheck();
    }
  }

  writeValue(value: any): void {
    this.selectedValue = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this._ctrlDisabled = isDisabled;
    this.cdr.markForCheck();
  }
}
